<script setup lang="ts">
import { useBackNavigation } from '@/composables/useBackNavigation'
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import type { Component } from 'vue'
import { RouterLink, useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { useUIStore } from '@/stores/ui'
import { useI18n } from 'vue-i18n'
import type { FeatureFlag } from '@/types'
import {
  PhHouse,
  PhBookBookmark,
  PhBroadcast,
  PhFiles,
  PhMegaphone,
  PhUser,
  PhMoney,
  PhRobot,
  PhSparkle,
  PhPlus,
  PhGear,
  PhSignOut,
  PhCaretUpDown,
  PhCaretRight,
  PhCreditCard,
  PhArrowsLeftRight,
  PhUsers,
  PhBook,
  PhQuestion,
  PhX,
  PhDatabase,
  PhStorefront,
  PhTruck,
  PhLock,
  PhChartBar,
  PhMagnifyingGlass,
  PhReceipt,
  PhClipboardText,
  PhTag,
  PhCaretLeft,
  PhPushPin,
  PhMagnet,
  PhCalendarDots,
  PhFlowArrow,
  PhHandshake,
  PhListMagnifyingGlass,
  PhBrain,
  PhImages,
  PhTray,
  PhChartLineUp,
  PhEnvelopeSimple,
  PhShareNetwork,
  PhFunnel,
  PhUsersThree,
  PhPaperPlaneTilt,
  PhEnvelopeOpen,
  PhMailbox
} from '@phosphor-icons/vue'
import { useAIAssistantStore } from '@/stores/aiAssistant'
import UpgradeModal from '@/components/subscription/UpgradeModal.vue'
import LogoFull from '@/components/layout/LogoFull.vue'
import LogoIcon from '@/components/layout/LogoIcon.vue'
import WorkspaceBadge from '@/components/layout/WorkspaceBadge.vue'
import { SETTINGS_BACK_ICON, resolveSettingsNavGroups, settingsItemRoute } from '@/config/settingsNav'
import { PROFILE_BACK_ICON, PROFILE_NAV_ITEMS } from '@/config/profileNav'
import { KNOWLEDGE_BACK_ICON, KNOWLEDGE_NAV_GROUPS, knowledgeItemRoute } from '@/config/knowledgeNav'
import { useSectionStatusStore } from '@/stores/sectionStatus'
import {
  PAGE_SETTINGS_BACK_ICON,
  PAGE_SETTINGS_NAV_GROUPS,
  pageSettingsItemRoute,
  pageEditorRoute,
} from '@/config/pageSettingsNav'
import {
  BLOG_SETTINGS_BACK_ICON,
  BLOG_SETTINGS_NAV_GROUPS,
  blogSettingsItemRoute,
  blogEditorRoute,
} from '@/config/blogSettingsNav'
import {
  EMAIL_SETTINGS_BACK_ICON,
  EMAIL_SETTINGS_NAV_GROUPS,
  emailSettingsItemRoute,
  emailEditorRoute,
} from '@/config/emailSettingsNav'
import {
  SOCIAL_SETTINGS_BACK_ICON,
  SOCIAL_SETTINGS_NAV_GROUPS,
  socialSettingsItemRoute,
  socialEditorRoute,
} from '@/config/socialSettingsNav'

// Types
interface MenuItem {
  name: string
  labelKey: string
  label?: string  // overrides labelKey when set (used for BR/LP items pending i18n keys)
  icon?: typeof PhHouse
  route?: string
  // When set, the menu item deep-links to ?channel=<channel> on `route`.
  // Used by the landing-page menu so Social/Emails/Blog each open the
  // unified /content page already narrowed to that channel.
  //
  // Named `channel`, not `tab`: `?tab=` is reserved platform-wide for the
  // TabStrip's own active tab (components/ui/TabStrip.vue keeps it in the
  // address so Back returns to the right tab). On /content the strip is the
  // STATUS filter, so the two would fight over one parameter.
  channel?: string
  type: 'link' | 'divider'
  hasAddButton?: boolean
  addRoute?: string
  adminOnly?: boolean
  requiredFeature?: FeatureFlag
}

// Stores
const route = useRoute()
const router = useRouter()
const { backTarget, keepReturnTo } = useBackNavigation()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const uiStore = useUIStore()
const aiAssistant = useAIAssistantStore()
const { t } = useI18n()

// Logo refs for random wave trigger
const logoFullRef = ref<InstanceType<typeof LogoFull> | null>(null)
const logoIconRef = ref<InstanceType<typeof LogoIcon> | null>(null)
let ambientTimer: ReturnType<typeof setTimeout> | null = null

// State
const hoveredItem = ref<string | null>(null)
const showUpgradeModal = ref(false)
// Feature flag of the locked menu item that triggered the modal — drives
// tier-targeted "Upgrade to X" copy in UpgradeModal. Null = generic copy.
// Takes any entry carrying a requiredFeature (main-nav items + settings-mode entries).
const upgradeFeature = ref<FeatureFlag | null>(null)
function openUpgradeModalFor(item: { requiredFeature?: FeatureFlag }): void {
  upgradeFeature.value = item.requiredFeature ?? null
  showUpgradeModal.value = true
}
const showUserMenu = ref(false)
const showWorkspacesSubmenu = ref(false)
const switchWorkspaceBtn = ref<HTMLElement | null>(null)
const workspaceSearchInput = ref<HTMLInputElement | null>(null)
const workspaceList = ref<HTMLElement | null>(null)
const workspaceSearchQuery = ref('')
const highlightedWorkspaceIndex = ref(0)
const submenuPosition = ref({ top: 0, left: 0, maxHeight: 400 })

// Computed
const workspaceId = computed(() => route.params.workspaceId as string)
// At compact widths, an unpinned icon strip opens while hovered and overlays
// the page. Pinning keeps it open and gives the page its full width.
const hoverExpanded = ref(false)
const isCollapsed = computed(() => uiStore.sidebarCollapsed && !(uiStore.isTablet && hoverExpanded.value))

const handleSidebarEnter = () => {
  if (uiStore.isTablet) hoverExpanded.value = true
}
const handleSidebarLeave = () => {
  hoverExpanded.value = false
}
const handleToggleClick = () => {
  if (uiStore.sidebarPinned) {
    uiStore.setSidebarPinned(false)
    hoverExpanded.value = false
    uiStore.setSidebarCollapsed(true)
    return
  }
  // Arrow shows "collapse" while hover-open; honour that instead of
  // pinning the sidebar open, which the store's toggle would do.
  if (uiStore.isTablet && hoverExpanded.value && uiStore.sidebarCollapsed) {
    hoverExpanded.value = false
    return
  }
  uiStore.toggleSidebar()
  hoverExpanded.value = false
}
const toggleSidebarPin = () => {
  uiStore.setSidebarPinned(!uiStore.sidebarPinned)
  if (uiStore.sidebarPinned) hoverExpanded.value = false
}
const workspaceName = computed(() => workspaceStore.currentWorkspace?.name || t('sidebar.myWorkspace'))
const creditsRemaining = computed(() => workspaceStore.creditsRemaining)
const monthlyTokens = computed(() => workspaceStore.entitlements?.limits?.monthlyTokens ?? 0)
const creditPercent = computed(() => {
  if (monthlyTokens.value <= 0 || creditsRemaining.value < 0) return 100
  return Math.min(100, Math.max(0, (creditsRemaining.value / monthlyTokens.value) * 100))
})
const circumference = 2 * Math.PI * 19
const progressOffset = computed(() => circumference * (1 - creditPercent.value / 100))

// Workspaces for switch workspace submenu - ensure current workspace is always included
const userWorkspaces = computed(() => {
  const allWorkspaces = [...(workspaceStore.workspaces || [])]
  const current = workspaceStore.currentWorkspace

  // If current workspace exists but isn't in the list, add it at the beginning
  if (current) {
    const existsInList = allWorkspaces.some(ws => String(ws.id) === String(current.id))
    if (!existsInList) {
      allWorkspaces.unshift(current)
    }
  } else if (workspaceId.value) {
    // If we have a workspaceId from route but no currentWorkspace loaded yet,
    // check if it's already in the list, if not create a placeholder
    const existsInList = allWorkspaces.some(ws => String(ws.id) === String(workspaceId.value))
    if (!existsInList) {
      allWorkspaces.unshift({
        id: workspaceId.value,
        name: workspaceName.value,
        language: 'en' as const,
        timezone: 'America/New_York',
        timezoneSetBy: 'default' as const
      })
    }
  }

  // Keep the current workspace first, then sort the others by name.
  return allWorkspaces.sort((a, b) => {
    const currentFirst = Number(String(b.id) === String(workspaceId.value))
      - Number(String(a.id) === String(workspaceId.value))
    return currentFirst || (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  })
})

const filteredWorkspaces = computed(() => {
  const query = workspaceSearchQuery.value.trim().toLowerCase()
  if (!query) return userWorkspaces.value
  return userWorkspaces.value.filter(ws =>
    ws.name?.toLowerCase().includes(query)
  )
})

const highlightedWorkspace = computed(() => filteredWorkspaces.value[highlightedWorkspaceIndex.value])

function highlightedWorkspaceLink(): HTMLAnchorElement | null {
  return workspaceList.value?.querySelector<HTMLAnchorElement>('.search-highlighted') ?? null
}

watch(filteredWorkspaces, () => {
  highlightedWorkspaceIndex.value = 0
  nextTick(() => highlightedWorkspaceLink()?.scrollIntoView({ block: 'nearest' }))
})

function handleWorkspaceKeydown(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  if (target !== workspaceSearchInput.value && !workspaceList.value?.contains(target)) return
  if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (!filteredWorkspaces.value.length) return
    const step = event.key === 'ArrowDown' ? 1 : -1
    highlightedWorkspaceIndex.value = Math.max(0, Math.min(
      highlightedWorkspaceIndex.value + step, filteredWorkspaces.value.length - 1,
    ))
    nextTick(() => {
      const link = highlightedWorkspaceLink()
      link?.scrollIntoView({ block: 'nearest' })
      if (target !== workspaceSearchInput.value) link?.focus({ preventScroll: true })
    })
  } else if (event.key === 'Enter') {
    event.preventDefault()
    // Use the same link as a mouse click so switching still reloads workspace state.
    highlightedWorkspaceLink()?.click()
  }
}

// Ambient animations — logo wave + icon wiggle at random intervals
function scheduleAmbient() {
  // Random interval between 30-50s
  ambientTimer = setTimeout(() => {
    // Trigger logo wave
    if (isCollapsed.value) {
      logoIconRef.value?.startWave()
    } else {
      logoFullRef.value?.startWave()
    }

    // Pick a random visible menu icon and wiggle it
    const icons = document.querySelectorAll('.menu-item:not(.locked) .menu-item-icon')
    if (icons.length > 0) {
      const icon = icons[Math.floor(Math.random() * icons.length)] as HTMLElement
      icon.classList.add('icon-wiggle')
      icon.addEventListener('animationend', () => icon.classList.remove('icon-wiggle'), { once: true })
    }

    scheduleAmbient()
  }, 30000 + Math.random() * 20000)
}

// Fetch workspaces and tokens on mount
onMounted(async () => {
  scheduleAmbient()
  if (workspaceStore.workspaces.length === 0) {
    try {
      await workspaceStore.fetchWorkspaces()
    } catch (e) {
      console.error('Failed to fetch workspaces:', e)
    }
  }
  // Fetch tokens if we have a workspace ID
  if (workspaceId.value) {
    try {
      await workspaceStore.fetchTokens(workspaceId.value)
    } catch (e) {
      console.error('Failed to fetch tokens:', e)
    }
  }
})

onUnmounted(() => {
  if (ambientTimer) clearTimeout(ambientTimer)
})

// Tablet: collapse the unpinned overlay after the user navigates away,
// so the new page is fully visible. Desktop honors the user's manual
// toggle and mobile uses the drawer pattern, so this is tablet-only.
watch(() => route.path, () => {
  if (uiStore.isTablet && !uiStore.sidebarPinned && !uiStore.sidebarCollapsed) {
    uiStore.setSidebarCollapsed(true)
  }
})

// Watch for workspace changes and fetch tokens
watch(workspaceId, async (newWorkspaceId) => {
  if (newWorkspaceId) {
    try {
      await workspaceStore.fetchTokens(newWorkspaceId)
    } catch (e) {
      console.error('Failed to fetch tokens:', e)
    }
  }
})

// Menu items configuration — the one product's full menu.
const websiteBuilderMenu: MenuItem[] = [
  { name: 'Home', labelKey: 'nav.home', icon: PhHouse, route: '/home', type: 'link' },
  { name: 'Website', labelKey: 'nav.divider.website', type: 'divider' },
  { name: 'Pages', labelKey: 'nav.pages', icon: PhBookBookmark, route: '/pages', type: 'link', hasAddButton: true, addRoute: '/pages?create=true', requiredFeature: 'pages' },
  { name: 'Forms', labelKey: 'nav.forms', icon: PhClipboardText, route: '/forms', type: 'link', requiredFeature: 'pages' },
  { name: 'Collections', labelKey: 'nav.collections', icon: PhDatabase, route: '/collections', type: 'link', hasAddButton: true, addRoute: '/collections?create=true', requiredFeature: 'collections' },
  { name: 'Catalog', labelKey: 'nav.catalog', icon: PhStorefront, route: '/catalog', type: 'link', hasAddButton: true, addRoute: '/catalog?create=true', requiredFeature: 'catalog' },
  { name: 'Discounts', labelKey: 'nav.discounts', icon: PhTag, route: '/discounts', type: 'link', hasAddButton: true, addRoute: '/discounts?create=true', requiredFeature: 'catalog' },
  { name: 'Shipping', labelKey: 'nav.shipping', icon: PhTruck, route: '/shipping', type: 'link', hasAddButton: true, addRoute: '/shipping?create=true', requiredFeature: 'catalog' },
  { name: 'Marketing', labelKey: 'nav.divider.marketing', type: 'divider' },
  { name: 'Business Brain', labelKey: 'nav.knowledge', icon: PhBrain, route: '/brain', type: 'link' },
  { name: 'Schedule', labelKey: 'nav.schedule', icon: PhCalendarDots, route: '/calendar', type: 'link', requiredFeature: 'scheduler', hasAddButton: true, addRoute: '/calendar?create=true' },
  { name: 'Campaigns', labelKey: 'nav.campaigns', icon: PhMegaphone, route: '/campaigns', type: 'link', requiredFeature: 'campaigns', hasAddButton: true, addRoute: '/campaigns?create=true' },
  { name: 'Content', labelKey: 'nav.content', icon: PhFiles, route: '/content', type: 'link', hasAddButton: true, addRoute: '/content?create=true' },
  { name: 'Media', labelKey: 'nav.mediaLibrary', icon: PhImages, route: '/media', type: 'link', hasAddButton: true, addRoute: '/media?create=true' },
  { name: 'Magnets', labelKey: 'nav.leadMagnets', icon: PhMagnet, route: '/magnets', type: 'link', hasAddButton: true, addRoute: '/magnets?create=true', requiredFeature: 'blog' },
  { name: 'Ads', labelKey: 'nav.ads', icon: PhBroadcast, route: '/ads', type: 'link', hasAddButton: true, addRoute: '/ads?create=true' },
  { name: 'Keywords', labelKey: 'nav.keywords', icon: PhListMagnifyingGlass, route: '/keywords', type: 'link', requiredFeature: 'blog', hasAddButton: true, addRoute: '/keywords?create=true' },
  // Existing Prospecting screens share the same plan access as before.
  { name: 'Prospecting', labelKey: 'nav.prospecting', type: 'divider', requiredFeature: 'prospecting' },
  { name: 'Research', labelKey: 'prospects.title', icon: PhMagnifyingGlass, route: '/prospecting/research', type: 'link', requiredFeature: 'prospecting' },
  { name: 'Prospects', labelKey: 'prospecting.tab.prospects', icon: PhUsersThree, route: '/prospecting/prospects', type: 'link', requiredFeature: 'prospecting' },
  { name: 'Outreach', labelKey: 'prospecting.tab.outreach', icon: PhPaperPlaneTilt, route: '/prospecting/outreach', type: 'link', requiredFeature: 'prospecting' },
  { name: 'Emails', labelKey: 'prospecting.tab.prepared', icon: PhEnvelopeOpen, route: '/prospecting/emails', type: 'link', requiredFeature: 'prospecting' },
  { name: 'Mailboxes', labelKey: 'prospecting.mailboxes.tab', icon: PhMailbox, route: '/prospecting/mailboxes', type: 'link', requiredFeature: 'prospecting' },
  { name: 'CRM', labelKey: 'nav.divider.crm', type: 'divider' },
  // Lists moved to Settings › Communication (2026-07-21); opted-in people
  // appear in Contacts behind a list filter.
  { name: 'Inbox', labelKey: 'nav.inbox', icon: PhTray, route: '/inbox', type: 'link', requiredFeature: 'inbox' },
  // Contacts is the one people directory. Old Leads links redirect into its
  // Sales follow-up saved view.
  { name: 'Contacts', labelKey: 'nav.contacts', icon: PhUser, route: '/contacts', type: 'link', requiredFeature: 'customers' },
  // Orders = the catalog-orders surface, promoted to be THE orders page and
  // moved to the top-level `/orders` URL 2026-07-02. Legacy `/orders` page removed.
  { name: 'Orders', labelKey: 'nav.orders', icon: PhReceipt, route: '/orders', type: 'link', requiredFeature: 'catalog' },
  { name: 'Deals', labelKey: 'nav.deals', icon: PhHandshake, route: '/deals', type: 'link', requiredFeature: 'customers' },
  { name: 'Automations', labelKey: 'nav.divider.automations', type: 'divider' },
  { name: 'Integrations', labelKey: 'nav.integrations', icon: PhRobot, route: '/integrations', type: 'link', requiredFeature: 'integrations' },
  { name: 'Sequences', labelKey: 'nav.sequences', icon: PhFlowArrow, route: '/sequences', type: 'link', requiredFeature: 'email.sequences', hasAddButton: true, addRoute: '/sequences?create=true' },
  { name: 'Agents', labelKey: 'nav.agents', icon: PhSparkle, route: '/agents', type: 'link', requiredFeature: 'integrations' },
  { name: 'Reports', labelKey: 'nav.divider.reports', type: 'divider' },
  { name: 'Overview', labelKey: 'nav.reportsOverview', icon: PhChartLineUp, route: '/reports', type: 'link' },
  { name: 'Form results', labelKey: 'formReport.title', icon: PhClipboardText, route: '/reports/forms', type: 'link', requiredFeature: 'pages' },
  { name: 'Journey', labelKey: 'people.funnel.title', icon: PhFunnel, route: '/reports/journey', type: 'link', requiredFeature: 'customers' },
  { name: 'Email results', labelKey: 'deliverability.title', icon: PhEnvelopeSimple, route: '/reports/email', type: 'link', requiredFeature: 'email' },
  { name: 'Social results', labelKey: 'socialResults.title', icon: PhShareNetwork, route: '/reports/social', type: 'link', requiredFeature: 'social' }
]

// Check if a menu item is locked (gated — shows lock icon)
function isLocked(item: MenuItem): boolean {
  if (!item.requiredFeature) return false
  return workspaceStore.getCapabilityAccess(item.requiredFeature) === 'gated'
}

// Resolve the display label — explicit `label` overrides the i18n key.
function itemLabel(item: MenuItem): string {
  return item.label || t(item.labelKey)
}

// In collapsed (icon) mode the tooltip is `position: fixed` so the menu
// can scroll vertically without clipping the tooltip horizontally — but
// fixed positioning doesn't know the item's screen Y, so we stamp it as
// a CSS var on hover and the tooltip reads it via `top: var(--pj-tooltip-y)`.
function handleItemEnter(event: MouseEvent, name: string): void {
  hoveredItem.value = name
  if (!isCollapsed.value) return
  const el = event.currentTarget as HTMLElement | null
  if (el) el.style.setProperty('--pj-tooltip-y', `${el.getBoundingClientRect().top}px`)
}

function applyMenuFilter(items: MenuItem[]): MenuItem[] {
  return items.filter(item => {
    // Admin-only items
    if (item.adminOnly && !authStore.isAdmin) return false
    // Hide items whose feature is set to 'hidden'
    if (item.requiredFeature && workspaceStore.getCapabilityAccess(item.requiredFeature) === 'hidden') return false
    return true
  })
}

const foundationMenuNames = new Set([
  'Home', 'Pages', 'Collections', 'Catalog', 'Discounts', 'Shipping',
  'Business Brain', 'Schedule', 'Content', 'Media', 'Leads', 'Contacts', 'Orders',
])
// Starter publishes social posts, email and blog (Free cannot), so it also
// needs Integrations to connect those accounts and the reports for what it sent.
const starterMenuNames = new Set([
  ...foundationMenuNames, 'Integrations', 'Overview', 'Social results', 'Email results', 'Form results',
])
const sidebarUpgrade = computed(() => {
  const tier = workspaceStore.entitlements?.tier || workspaceStore.currentWorkspace?.workspaceTier
  if (tier === 'scale') {
    return {
      title: t('sidebar.upgrade.studioTitle'),
      description: t('sidebar.upgrade.studio'),
      href: 'https://profitjam.io/speak-with-us',
    }
  }
  if (tier !== 'free' && tier !== 'starter' && tier !== 'growth') return null
  return {
    title: t(`sidebar.upgrade.${tier}Title`),
    description: t(`sidebar.upgrade.${tier}`),
    href: null,
  }
})
const activeMenu = computed(() => {
  const tier = workspaceStore.entitlements?.tier || workspaceStore.currentWorkspace?.workspaceTier
  const allowed = tier === 'free' ? foundationMenuNames : tier === 'starter' ? starterMenuNames : null
  const items = allowed
    ? websiteBuilderMenu.filter(item => item.type === 'divider' || allowed.has(item.name))
    : websiteBuilderMenu
  const visible = applyMenuFilter(items)
  // Drop headings that no longer have any links beneath them.
  return visible.filter((item, index) => item.type !== 'divider'
    || visible[index + 1]?.type === 'link')
})

// Helper functions
function getFullRoute(itemRoute: string): string {
  return `/${workspaceId.value}${itemRoute}`
}

function isActive(item: MenuItem): boolean {
  if (!item.route) return false
  const fullRoute = getFullRoute(item.route)
  if (item.route === '/reports') return route.path === fullRoute || route.path === fullRoute + '/'
  const pathMatches = route.path === fullRoute || route.path.startsWith(fullRoute + '/')
    || (item.name === 'Contacts' && route.path.startsWith(`/${workspaceId.value}/customers/`))
  if (!pathMatches) return false
  // Items that deep-link to a specific channel only light up when that
  // channel is the active one — otherwise Social, Emails and Blog would all
  // appear active simultaneously on /content.
  if (item.channel) return route.query.channel === item.channel
  return true
}

// Resolve the router-link target. `channel`-bearing items emit a location
// object so Vue Router writes `?channel=…` into the URL; everything else
// stays as a simple path string.
function itemTo(item: MenuItem): string | { path: string; query: Record<string, string> } {
  const fullRoute = getFullRoute(item.route!)
  if (item.channel) return { path: fullRoute, query: { channel: item.channel } }
  return fullRoute
}

function toggleUserMenu(): void {
  showUserMenu.value = !showUserMenu.value
  if (!showUserMenu.value) {
    showWorkspacesSubmenu.value = false
  }
}

function closeUserMenu(): void {
  showUserMenu.value = false
  showWorkspacesSubmenu.value = false
}

// ── Settings mode ─────────────────────────────────────────────────────────
// On settings routes (route meta `navVariant: 'settings'`) the sidebar swaps
// its content in place: a back row + the settings menu replace the main nav,
// and the logo row + workspace footer are hidden. Same aside element, so the
// mobile drawer / tablet overlay / collapse / encapsulated-mode CSS all keep
// applying unchanged.
const isSettingsNav = computed(() => route.meta.navVariant === 'settings')

interface SettingsEntry {
  id: string
  label: string
  icon?: Component
  route: string
  locked: boolean
  requiredFeature?: FeatureFlag
  type: 'link' | 'divider'
}

// Built from the shared config (config/settingsNav.ts) so this menu and the
// settings page's slug list can't drift. Hidden-on-this-plan items disappear;
// gated ones render with the main nav's lock + upgrade-modal pattern.
const settingsMenu = computed<SettingsEntry[]>(() => {
  const resolved = resolveSettingsNavGroups({
    getFeatureAccess: (flag) => workspaceStore.getCapabilityAccess(flag),
  })
  const entries: SettingsEntry[] = []
  for (const group of resolved) {
    entries.push({
      id: `group:${group.labelKey}`,
      label: t(group.labelKey, group.labelFallback),
      route: '',
      locked: false,
      type: 'divider',
    })
    for (const item of group.items) {
      if (item.access === 'hidden') continue
      entries.push({
        id: item.id,
        label: t(item.labelKey, item.labelFallback),
        icon: item.icon,
        route: settingsItemRoute(item.id),
        locked: item.access === 'gated',
        requiredFeature: item.requiredFeature,
        type: 'link',
      })
    }
  }
  return entries
})

// Settings items highlight on the EXACT path only — General owns the bare
// /settings URL plus its /settings/general alias; every other item owns
// exactly /settings/<id>. (The main nav's startsWith matching would light
// General up on every tab.)
function isSettingsEntryActive(entry: SettingsEntry): boolean {
  const base = `/${workspaceId.value}/settings`
  if (entry.id === 'general') return route.path === base || route.path === `${base}/general`
  return route.path === `${base}/${entry.id}`
}

// Back row — return to wherever the user was before opening settings (same
// workspace only; the router records it), falling back to Home for deep
// links and workspace switches.
function settingsBackTo(): string {
  const returnTo = uiStore.settingsReturnTo
  return returnTo && returnTo.startsWith(`/${workspaceId.value}/`) ? returnTo : getFullRoute('/home')
}

// Profile uses the same section sidebar and return behavior as Settings.
const isProfileNav = computed(() => route.meta.navVariant === 'profile')
const profileMenu = computed<SettingsEntry[]>(() => PROFILE_NAV_ITEMS
  .filter(item => !item.requiredFeature || workspaceStore.getCapabilityAccess(item.requiredFeature) !== 'hidden')
  .map(item => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    route: item.route,
    locked: !!item.requiredFeature && workspaceStore.getCapabilityAccess(item.requiredFeature) === 'gated',
    requiredFeature: item.requiredFeature,
    type: 'link' as const,
  })))

function isProfileEntryActive(entry: SettingsEntry): boolean {
  const path = route.path.endsWith('/') ? route.path.slice(0, -1) : route.path
  return path === getFullRoute(entry.route)
}

function profileBackTo(): string {
  const returnTo = uiStore.profileReturnTo
  return returnTo && returnTo.startsWith(getFullRoute('/')) ? returnTo : getFullRoute('/home')
}

// ── Knowledge mode ─────────────────────────────────────────────────────────
// Parallel to settings mode above: on knowledge routes (meta
// `navVariant: 'knowledge'`) the sidebar swaps its content for a back row + the
// knowledge menu, and hides the logo row + workspace footer. Same aside
// element, so the mobile drawer / tablet overlay / collapse CSS all keep
// applying unchanged.
const isKnowledgeNav = computed(() => route.meta.navVariant === 'knowledge')

// Built from the shared config (config/knowledgeNav.ts) so this menu and the
// knowledge page's slug list can't drift. No plan gating in Knowledge, so
// entries reuse SettingsEntry's shape. `locked` = the section's row is not
// green yet (Jens 2026-08-18: sections are reachable only when done) —
// locked entries grey out and don't navigate; KnowledgePage's guard closes
// the direct-address door for the same rule.
const sectionStatusStore = useSectionStatusStore()
const knowledgeMenu = computed<SettingsEntry[]>(() => {
  const entries: SettingsEntry[] = []
  for (const group of KNOWLEDGE_NAV_GROUPS) {
    entries.push({
      id: `group:${group.label}`,
      label: group.label,
      route: '',
      locked: false,
      type: 'divider',
    })
    for (const item of group.items) {
      // The Strategy document stays listed but greyed until the WHOLE brain
      // is built (Jens 2026-08-18) — a half-empty strategy is unreachable.
      const locked = item.id === 'strategy'
        ? !sectionStatusStore.strategyReady
        : sectionStatusStore.isGatedSlug(item.id) && !sectionStatusStore.isDone(item.id)
      entries.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        route: knowledgeItemRoute(item.id),
        locked,
        type: 'link',
      })
    }
  }
  return entries
})

// Knowledge items highlight on the EXACT path only — each owns exactly
// /<workspace>/knowledge/<id>.
function isKnowledgeEntryActive(entry: SettingsEntry): boolean {
  return route.path === `/${workspaceId.value}/brain/${entry.id}`
}

// Back row — return to wherever the user was before opening knowledge (same
// workspace only; the router records it), falling back to Home for deep links
// and workspace switches.
function knowledgeBackTo(): string {
  const returnTo = uiStore.knowledgeReturnTo
  return returnTo && returnTo.startsWith(`/${workspaceId.value}/`) ? returnTo : getFullRoute('/home')
}

// ── Page-settings mode ──────────────────────────────────────────────────────
// Parallel to knowledge mode above: on the per-page settings route (meta
// `navVariant: 'page-settings'`) the sidebar swaps its content for a back row +
// the page-settings menu, and hides the logo row + workspace footer. Same aside
// element, so mobile drawer / tablet overlay / collapse CSS all keep applying.
//
// The one difference from the other three variants: every entry is scoped to
// ONE page, so the routes carry the page id from the URL.
const isPageSettingsNav = computed(() => route.meta.navVariant === 'page-settings')
const pageSettingsPageId = computed(() => String(route.params.pageId || ''))

// Built from the shared config (config/pageSettingsNav.ts) so this menu and the
// page-settings page's slug list can't drift. No plan gating here, so entries
// reuse SettingsEntry's shape with locked always false.
const pageSettingsMenu = computed<SettingsEntry[]>(() => {
  const entries: SettingsEntry[] = []
  const pageId = pageSettingsPageId.value
  if (!pageId) return entries
  for (const group of PAGE_SETTINGS_NAV_GROUPS) {
    entries.push({
      id: `group:${group.label}`,
      label: group.label,
      route: '',
      locked: false,
      type: 'divider',
    })
    for (const item of group.items) {
      entries.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        route: pageSettingsItemRoute(pageId, item.id),
        locked: false,
        type: 'link',
      })
    }
  }
  return entries
})

// Page-settings items highlight on the EXACT path only — Page info owns the
// bare /<page>/settings URL plus its /settings/info alias; every other item
// owns exactly /<page>/settings/<id>.
function isPageSettingsEntryActive(entry: SettingsEntry): boolean {
  const base = `/${workspaceId.value}/${pageSettingsPageId.value}/settings`
  if (entry.id === 'info') return route.path === base || route.path === `${base}/info`
  return route.path === `${base}/${entry.id}`
}

// Back row — always the builder for THIS page. Unlike settings/knowledge
// there's nothing to remember: the destination is in the URL.
function pageSettingsBackTo(): string {
  const pageId = pageSettingsPageId.value
  return pageId ? getFullRoute(pageEditorRoute(pageId)) : getFullRoute('/pages')
}

// ── Blog-settings mode ─────────────────────────────────────────────────────
// Uses the same sidebar shell as Page settings, but each route is scoped to a
// Content row. Post info owns Content metadata; Custom code targets the Blog
// detail page selected by BlogSettingsPage.
const isBlogSettingsNav = computed(() => route.meta.navVariant === 'blog-settings')
const blogSettingsContentId = computed(() => String(route.params.contentId || ''))

const blogSettingsMenu = computed<SettingsEntry[]>(() => {
  const entries: SettingsEntry[] = []
  const contentId = blogSettingsContentId.value
  if (!contentId) return entries
  for (const group of BLOG_SETTINGS_NAV_GROUPS) {
    entries.push({
      id: `group:${group.label}`,
      label: group.label,
      route: '',
      locked: false,
      type: 'divider',
    })
    for (const item of group.items) {
      entries.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        route: blogSettingsItemRoute(contentId, item.id),
        locked: false,
        type: 'link',
      })
    }
  }
  return entries
})

function isBlogSettingsEntryActive(entry: SettingsEntry): boolean {
  const base = `/${workspaceId.value}/blog/${blogSettingsContentId.value}/settings`
  if (entry.id === 'info') return route.path === base || route.path === `${base}/info`
  return route.path === `${base}/${entry.id}`
}

function blogSettingsBackTo(): string {
  const contentId = blogSettingsContentId.value
  return contentId ? getFullRoute(blogEditorRoute(contentId)) : getFullRoute('/content?channel=blog')
}

const isSocialSettingsNav = computed(() => route.meta.navVariant === 'social-settings')
const socialSettingsContentId = computed(() => String(route.params.contentId || ''))
const socialSettingsMenu = computed<SettingsEntry[]>(() => SOCIAL_SETTINGS_NAV_GROUPS.flatMap(group => [
  { id: `group:${group.label}`, label: group.label, route: '', locked: false, type: 'divider' as const },
  ...group.items.map(item => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    route: socialSettingsItemRoute(socialSettingsContentId.value, item.id),
    locked: false,
    type: 'link' as const,
  })),
]))

function socialSettingsBackTo(): string {
  return getFullRoute(socialEditorRoute(socialSettingsContentId.value))
}

// ── Email-settings mode ────────────────────────────────────────────────
// The Email editor's cog opens a standalone settings screen just like Blog.
// Its menu is config-driven so future Email settings can join Email info
// without returning metadata controls to the editor inspector.
const isEmailSettingsNav = computed(() => route.meta.navVariant === 'email-settings')
const emailSettingsContentId = computed(() => String(route.params.contentId || ''))

const emailSettingsMenu = computed<SettingsEntry[]>(() => {
  const entries: SettingsEntry[] = []
  const contentId = emailSettingsContentId.value
  if (!contentId) return entries
  for (const group of EMAIL_SETTINGS_NAV_GROUPS) {
    entries.push({
      id: `group:${group.label}`,
      label: group.label,
      route: '',
      locked: false,
      type: 'divider',
    })
    for (const item of group.items) {
      entries.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        route: emailSettingsItemRoute(contentId, item.id),
        locked: false,
        type: 'link',
      })
    }
  }
  return entries
})

function isEmailSettingsEntryActive(entry: SettingsEntry): boolean {
  const base = `/${workspaceId.value}/email/${emailSettingsContentId.value}/settings`
  if (entry.id === 'info') return route.path === base || route.path === `${base}/info`
  return route.path === `${base}/${entry.id}`
}

function emailSettingsBackTo(): string {
  const contentId = emailSettingsContentId.value
  return contentId ? getFullRoute(emailEditorRoute(contentId)) : getFullRoute('/content?channel=email')
}

// Keep each section's back action above the scrollable menu, like the logo
// in the main navigation. It must remain reachable when the icon strip is
// collapsed or the menu is scrolled down.
const sectionBack = computed<{ label: string; to: RouteLocationRaw; icon: Component; id: string } | null>(() => {
  if (isSettingsNav.value) return { label: t('sidebar.settings'), to: settingsBackTo(), icon: SETTINGS_BACK_ICON, id: 'settings-back' }
  if (isProfileNav.value) return { label: t('sidebar.profile'), to: profileBackTo(), icon: PROFILE_BACK_ICON, id: 'profile-back' }
  if (isKnowledgeNav.value) return { label: t('nav.knowledge', 'Business Brain'), to: knowledgeBackTo(), icon: KNOWLEDGE_BACK_ICON, id: 'knowledge-back' }
  if (isPageSettingsNav.value) return { label: t('pageSettings.backToEditor', 'Back to editor'), to: pageSettingsBackTo(), icon: PAGE_SETTINGS_BACK_ICON, id: 'page-settings-back' }
  if (isBlogSettingsNav.value) return { label: t('sidebar.backToBlogPost'), to: backTarget.value || blogSettingsBackTo(), icon: BLOG_SETTINGS_BACK_ICON, id: 'blog-settings-back' }
  if (isSocialSettingsNav.value) return { label: t('sidebar.backToSocialPost'), to: socialSettingsBackTo(), icon: SOCIAL_SETTINGS_BACK_ICON, id: 'social-settings-back' }
  if (isEmailSettingsNav.value) return { label: t('emailDelivery.back'), to: emailSettingsBackTo(), icon: EMAIL_SETTINGS_BACK_ICON, id: 'email-settings-back' }
  return null
})

function handleAskQuestion(): void {
  closeUserMenu()
  aiAssistant.openPanel()
}

function handleLogout(): void {
  closeUserMenu()
  router.push('/auth/logout')
}

function toggleWorkspacesSubmenu(): void {
  if (!showWorkspacesSubmenu.value && switchWorkspaceBtn.value) {
    const rect = switchWorkspaceBtn.value.getBoundingClientRect()
    const submenuWidth = 220
    let left = rect.right + 4
    // Flip the submenu to the left side of the trigger if it would overflow the viewport
    // (happens on mobile, where the parent menu spans the full screen width)
    if (left + submenuWidth > window.innerWidth - 8) {
      left = Math.max(8, rect.left - submenuWidth - 4)
    }
    submenuPosition.value = {
      top: rect.top,
      left,
      maxHeight: window.innerHeight - rect.top - 16
    }
    workspaceSearchQuery.value = ''
    highlightedWorkspaceIndex.value = 0
    showWorkspacesSubmenu.value = true
    nextTick(() => (workspaceSearchInput.value ?? highlightedWorkspaceLink())?.focus())
  } else {
    showWorkspacesSubmenu.value = false
  }
}

function closeMobileMenu(): void {
  uiStore.closeMobileSidebar()
}
</script>

<template>
  <aside
    class="sidebar"
    :class="{ collapsed: isCollapsed }"
    @mouseenter="handleSidebarEnter"
    @mouseleave="handleSidebarLeave"
  >
    <!-- Logo Section — hidden in the section-takeover modes (settings /
         knowledge / page settings), where the back row takes the top slot and the
         mobile close button moves onto it -->
    <div v-if="!sectionBack" class="sidebar-logo">
      <router-link :to="getFullRoute('/home')" class="logo-link" @click="uiStore.isMobile && closeMobileMenu()">
        <!-- Full logo when expanded -->
        <LogoFull v-if="!isCollapsed" ref="logoFullRef" class="logo-full" />
        <!-- Icon logo with border when collapsed -->
        <div v-else class="logo-icon-wrapper">
          <LogoIcon ref="logoIconRef" class="logo-icon" />
        </div>
      </router-link>
      <!-- Mobile close button -->
      <button v-if="uiStore.isMobile" class="mobile-close-btn" @click="closeMobileMenu" :aria-label="t('sidebar.closeMenu')">
        <PhX :size="20" weight="bold" />
      </button>
    </div>

    <!-- Collapse/expand toggle for desktop + tablet. Mobile uses the drawer
         pattern instead. An unpinned tablet sidebar overlays the page and
         auto-collapses on navigation; a pinned one keeps its own page space. -->
    <button
      v-if="!uiStore.isMobile"
      class="sidebar-collapse-toggle"
      :class="{ collapsed: isCollapsed }"
      :aria-label="isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')"
      :aria-pressed="isCollapsed"
      @click="handleToggleClick"
    >
      <PhCaretLeft :size="12" weight="bold" class="toggle-arrow" />
    </button>

    <button
      v-if="uiStore.isTablet && !isCollapsed"
      class="sidebar-pin-toggle"
      :class="{ pinned: uiStore.sidebarPinned }"
      :aria-label="uiStore.sidebarPinned ? t('sidebar.unpinSidebar') : t('sidebar.pinSidebar')"
      :title="uiStore.sidebarPinned ? t('sidebar.unpinSidebar') : t('sidebar.pinSidebar')"
      :aria-pressed="uiStore.sidebarPinned"
      @click="toggleSidebarPin"
    >
      <PhPushPin :size="16" :weight="uiStore.sidebarPinned ? 'fill' : 'regular'" />
    </button>

    <!-- Section back action stays visible while the menu below scrolls. -->
    <div v-if="sectionBack" class="settings-back-row">
      <router-link
        class="menu-item"
        :aria-label="sectionBack.label"
        :to="sectionBack.to"
        @click="closeMobileMenu"
        @mouseenter="handleItemEnter($event, sectionBack.id)"
        @mouseleave="hoveredItem = null"
      >
        <component :is="sectionBack.icon" class="menu-item-icon" :size="20" weight="regular" />
        <span v-if="!isCollapsed" class="menu-item-label">{{ sectionBack.label }}</span>
        <span v-if="isCollapsed" class="menu-tooltip">{{ sectionBack.label }}</span>
      </router-link>
      <button v-if="uiStore.isMobile" class="mobile-close-btn" @click="closeMobileMenu" :aria-label="t('sidebar.closeMenu')">
        <PhX :size="20" weight="bold" />
      </button>
    </div>

    <!-- Main Navigation -->
    <nav class="menu-items" :class="{ 'has-section-back': sectionBack }">
      <!-- Settings mode — the settings menu replaces the main nav -->
      <template v-if="isSettingsNav">
        <template v-for="entry in settingsMenu" :key="entry.id">
          <!-- Group label -->
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>

          <!-- Locked (plan-gated) settings item -->
          <button
            v-else-if="entry.locked"
            class="menu-item locked"
            :aria-label="entry.label"
            @click="openUpgradeModalFor(entry)"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
          >
            <component
              :is="entry.icon"
              class="menu-item-icon"
              :size="20"
              weight="regular"
            />
            <span v-if="!isCollapsed" class="menu-item-label">
              {{ entry.label }}
            </span>
            <PhLock v-if="!isCollapsed" class="menu-item-lock" :size="14" weight="regular" />
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </button>

          <!-- Settings item link -->
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item"
            :class="{ active: isSettingsEntryActive(entry) }"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component
              :is="entry.icon"
              class="menu-item-icon"
              :size="20"
              weight="regular"
            />
            <span v-if="!isCollapsed" class="menu-item-label">
              {{ entry.label }}
            </span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <!-- Profile sections use the same links and drawer behavior as Settings. -->
      <template v-else-if="isProfileNav">
        <template v-for="entry in profileMenu" :key="entry.id">
          <button
            v-if="entry.locked"
            class="menu-item locked"
            :aria-label="entry.label"
            @click="openUpgradeModalFor(entry)"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
          >
            <component :is="entry.icon" class="menu-item-icon" :size="20" weight="regular" />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
            <PhLock v-if="!isCollapsed" class="menu-item-lock" :size="14" weight="regular" />
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </button>
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item"
            :class="{ active: isProfileEntryActive(entry) }"
            :aria-current="isProfileEntryActive(entry) ? 'page' : undefined"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component :is="entry.icon" class="menu-item-icon" :size="20" weight="regular" />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <!-- Knowledge mode — the knowledge menu replaces the main nav. -->
      <template v-else-if="isKnowledgeNav">
        <template v-for="entry in knowledgeMenu" :key="entry.id">
          <!-- Group label -->
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>

          <!-- Locked section: not done yet — greyed, goes nowhere. -->
          <div
            v-else-if="entry.locked"
            class="menu-item locked knowledge-locked"
            :aria-label="t('sidebar.itemNotReady', { label: entry.label })"
          >
            <component
              :is="entry.icon"
              class="menu-item-icon"
              :size="20"
              weight="regular"
            />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
          </div>

          <!-- Knowledge item link -->
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item"
            :class="{ active: isKnowledgeEntryActive(entry) }"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component
              :is="entry.icon"
              class="menu-item-icon"
              :size="20"
              weight="regular"
            />
            <span v-if="!isCollapsed" class="menu-item-label">
              {{ entry.label }}
            </span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <!-- Page-settings mode — the menu replaces the main nav. -->
      <template v-else-if="isPageSettingsNav">
        <template v-for="entry in pageSettingsMenu" :key="entry.id">
          <!-- Group label -->
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>

          <!-- Page-settings item link -->
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item"
            :class="{ active: isPageSettingsEntryActive(entry) }"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component
              :is="entry.icon"
              class="menu-item-icon"
              :size="20"
              weight="regular"
            />
            <span v-if="!isCollapsed" class="menu-item-label">
              {{ entry.label }}
            </span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <template v-else-if="isBlogSettingsNav">
        <template v-for="entry in blogSettingsMenu" :key="entry.id">
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>
          <router-link
            v-else
            :to="keepReturnTo(getFullRoute(entry.route))"
            class="menu-item"
            :class="{ active: isBlogSettingsEntryActive(entry) }"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component :is="entry.icon" class="menu-item-icon" :size="20" weight="regular" />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <template v-else-if="isSocialSettingsNav">
        <template v-for="entry in socialSettingsMenu" :key="entry.id">
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item active"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component :is="entry.icon" class="menu-item-icon" :size="20" weight="regular" />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <template v-else-if="isEmailSettingsNav">
        <template v-for="entry in emailSettingsMenu" :key="entry.id">
          <div v-if="entry.type === 'divider'" class="menu-divider">
            <span v-if="!isCollapsed">{{ entry.label }}</span>
          </div>
          <router-link
            v-else
            :to="getFullRoute(entry.route)"
            class="menu-item"
            :class="{ active: isEmailSettingsEntryActive(entry) }"
            :aria-label="entry.label"
            @mouseenter="handleItemEnter($event, entry.id)"
            @mouseleave="hoveredItem = null"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <component :is="entry.icon" class="menu-item-icon" :size="20" weight="regular" />
            <span v-if="!isCollapsed" class="menu-item-label">{{ entry.label }}</span>
            <span v-if="isCollapsed" class="menu-tooltip">{{ entry.label }}</span>
          </router-link>
        </template>
      </template>

      <template v-else>
      <template v-for="item in activeMenu" :key="item.name">
        <!-- Divider -->
        <div v-if="item.type === 'divider'" class="menu-divider">
          <span v-if="!isCollapsed">{{ itemLabel(item) }}</span>
        </div>

        <!-- Locked Menu Item -->
        <button
          v-else-if="isLocked(item)"
          class="menu-item locked"
          :aria-label="itemLabel(item)"
          @click="openUpgradeModalFor(item)"
          @mouseenter="handleItemEnter($event, item.name)"
          @mouseleave="hoveredItem = null"
        >
          <component
            :is="item.icon"
            class="menu-item-icon"
            :size="20"
            weight="regular"
          />
          <span v-if="!isCollapsed" class="menu-item-label">
            {{ itemLabel(item) }}
          </span>
          <PhLock v-if="!isCollapsed" class="menu-item-lock" :size="14" weight="regular" />
          <span v-if="isCollapsed" class="menu-tooltip">{{ itemLabel(item) }}</span>
        </button>

        <!-- Menu Item Link -->
        <div
          v-else
          class="menu-item-group"
          :class="{ 'has-add': item.hasAddButton && !isCollapsed }"
          @mouseenter="handleItemEnter($event, item.name)"
          @mouseleave="hoveredItem = null"
        >
        <router-link
          :to="itemTo(item)"
          class="menu-item"
          :class="{ active: isActive(item) }"
          :aria-label="itemLabel(item)"
          @click="uiStore.isMobile && closeMobileMenu()"
        >
          <component
            :is="item.icon"
            class="menu-item-icon"
            :size="20"
            weight="regular"
          />
          <span v-if="!isCollapsed" class="menu-item-label">
            {{ itemLabel(item) }}
          </span>
          <span v-if="isCollapsed" class="menu-tooltip">{{ itemLabel(item) }}</span>
        </router-link>
          <router-link
            v-if="item.hasAddButton && !isCollapsed"
            :to="getFullRoute(item.addRoute || item.route!)"
            class="menu-item-add-btn"
            :class="{ visible: hoveredItem === item.name || isActive(item) }"
            :aria-label="itemLabel(item)"
            @click="uiStore.isMobile && closeMobileMenu()"
          >
            <PhPlus :size="14" weight="bold" />
          </router-link>
        </div>
      </template>
      </template>
    </nav>

    <div
      v-if="sidebarUpgrade && !sectionBack"
      class="sidebar-upgrade-slot"
      :inert="isCollapsed"
      :aria-hidden="isCollapsed ? true : undefined"
    >
      <div class="sidebar-upgrade-clip">
        <component
          :is="sidebarUpgrade.href ? 'a' : RouterLink"
          :to="sidebarUpgrade.href ? undefined : getFullRoute('/subscription')"
          :href="sidebarUpgrade.href || undefined"
          class="sidebar-upgrade-card"
          :aria-label="[sidebarUpgrade.title, sidebarUpgrade.description].filter(Boolean).join(' ')"
          @click="uiStore.isMobile && closeMobileMenu()"
        >
          <div class="sidebar-upgrade-copy">
            <strong v-if="sidebarUpgrade.title">{{ sidebarUpgrade.title }}</strong>
            <p>{{ sidebarUpgrade.description }}</p>
          </div>
          <span class="sidebar-upgrade-arrow" aria-hidden="true"><PhCaretRight :size="16" weight="bold" /></span>
        </component>
      </div>
    </div>

    <!-- User/Workspace Section — hidden in the section-takeover modes -->
    <div v-if="!sectionBack" class="sidebar-footer">
      <div
        class="user-workspace-selector"
        :class="{ active: showUserMenu }"
        @click="toggleUserMenu"
      >
        <div class="workspace-avatar-wrapper">
          <svg class="credit-ring" viewBox="0 0 42 42">
            <circle cx="21" cy="21" r="19" fill="none" stroke="#D6D6D6" stroke-width="2.5" />
            <circle cx="21" cy="21" r="19" fill="none" stroke="#021E30" stroke-width="2.5" stroke-linecap="round" :stroke-dasharray="circumference" :stroke-dashoffset="progressOffset" transform="rotate(-90 21 21)" />
          </svg>
          <WorkspaceBadge
            :workspace-id="workspaceId"
            :name="workspaceName"
            :branding="workspaceStore.currentWorkspace?.branding"
          />
        </div>
        <div v-if="!isCollapsed" class="workspace-info">
          <span class="workspace-name-label">{{ workspaceName }}</span>
          <span class="my-workspace-label">{{ t('sidebar.myWorkspace') }}</span>
        </div>
        <PhCaretUpDown v-if="!isCollapsed" class="caret-icon" :size="16" />
      </div>

      <!-- Context Menu -->
      <Transition name="menu-fade">
        <div v-if="showUserMenu" class="user-context-menu" @click.stop>
          <!-- Credits Section -->
          <div class="credits-section">
            <div class="credits-header">
              <svg class="credits-ring" viewBox="0 0 42 42" width="18" height="18">
                <circle cx="21" cy="21" r="19" fill="none" stroke="#D6D6D6" stroke-width="3" />
                <circle cx="21" cy="21" r="19" fill="none" stroke="#021E30" stroke-width="3" stroke-linecap="round" :stroke-dasharray="circumference" :stroke-dashoffset="progressOffset" transform="rotate(-90 21 21)" />
              </svg>
              <span class="credits-label">{{ t('sidebar.credits') }}</span>
              <router-link class="upgrade-btn" :to="getFullRoute('/subscription')" @click="closeUserMenu">{{ t('common.upgrade') }}</router-link>
            </div>
            <div class="credits-value">
              <span class="credits-sublabel">{{ t('sidebar.totalRemaining') }}</span>
              <span class="credits-number">{{ creditsRemaining < 0 ? t('sidebar.unlimited') : creditsRemaining.toLocaleString() }}</span>
            </div>
          </div>

          <div class="context-menu-divider"></div>

          <!-- Profile & Subscription -->
          <router-link class="context-menu-item" :to="getFullRoute('/profile')" @click="closeUserMenu">
            <PhUser :size="18" />
            <span>{{ t('sidebar.profile') }}</span>
          </router-link>

          <router-link class="context-menu-item" :to="getFullRoute('/subscription')" @click="closeUserMenu">
            <PhCreditCard :size="18" />
            <span>{{ t('sidebar.subscription') }}</span>
          </router-link>

          <router-link class="context-menu-item" :to="getFullRoute('/usage')" @click="closeUserMenu">
            <PhChartBar :size="18" />
            <span>{{ t('sidebar.usage') }}</span>
          </router-link>

          <!-- Switch Workspace with Submenu -->
          <div class="context-menu-item-wrapper">
            <button
              ref="switchWorkspaceBtn"
              class="context-menu-item has-submenu"
              @click="toggleWorkspacesSubmenu"
            >
              <PhArrowsLeftRight :size="18" />
              <span>{{ t('sidebar.switchWorkspace') }}</span>
              <PhCaretRight :size="14" class="submenu-arrow" />
            </button>

            <!-- Workspaces Submenu -->
            <Transition name="submenu-fade">
              <div
                v-if="showWorkspacesSubmenu"
                class="workspaces-submenu"
                :style="{ top: submenuPosition.top + 'px', left: submenuPosition.left + 'px', maxHeight: submenuPosition.maxHeight + 'px' }"
                @keydown.stop="handleWorkspaceKeydown"
              >
                <div v-if="userWorkspaces.length > 10" class="workspace-search">
                  <PhMagnifyingGlass :size="14" class="workspace-search-icon" />
                  <input
                    ref="workspaceSearchInput"
                    v-model="workspaceSearchQuery"
                    type="text"
                    :placeholder="t('sidebar.searchWorkspaces')"
                    :aria-label="t('sidebar.searchWorkspaces')"
                    aria-controls="workspace-switcher-list"
                    :aria-describedby="highlightedWorkspace ? `workspace-switcher-${highlightedWorkspace.id}` : undefined"
                    class="workspace-search-input"
                  />
                </div>
                <div id="workspace-switcher-list" ref="workspaceList" class="workspaces-list">
                  <!-- Workspace changes retain their full context reset. -->
                  <a
                    v-for="(ws, index) in filteredWorkspaces"
                    :key="ws.id"
                    :id="`workspace-switcher-${ws.id}`"
                    class="workspace-option"
                    :class="{
                      active: String(ws.id) === String(workspaceId),
                      'search-highlighted': index === highlightedWorkspaceIndex,
                    }"
                    :aria-current="String(ws.id) === String(workspaceId) ? 'true' : undefined"
                    :href="`/${ws.id}/home`"
                    @focus="highlightedWorkspaceIndex = index"
                    @click="closeUserMenu"
                  >
                    <WorkspaceBadge
                      :workspace-id="ws.id"
                      :name="ws.name"
                      :branding="ws.branding"
                      size="small"
                    />
                    <span class="workspace-option-text">
                      <span class="workspace-option-name">{{ ws.name }}</span>
                      <span v-if="String(ws.id) === String(workspaceId)" class="workspace-option-current">{{ t('sidebar.currentWorkspace') }}</span>
                    </span>
                  </a>
                  <div v-if="filteredWorkspaces.length === 0 && workspaceSearchQuery.trim()" class="no-workspaces">
                    {{ t('sidebar.noResults') }}
                  </div>
                  <div v-else-if="userWorkspaces.length === 0" class="no-workspaces">
                    {{ t('sidebar.noWorkspaces') }}
                  </div>
                </div>
                <div class="submenu-divider"></div>
                <router-link class="workspace-option create-workspace" to="/create" @click="closeUserMenu">
                  <div class="workspace-option-icon">
                    <PhPlus :size="14" weight="bold" />
                  </div>
                  <span>{{ t('sidebar.createWorkspace') }}</span>
                </router-link>
              </div>
            </Transition>
          </div>

          <div class="context-menu-divider"></div>

          <!-- Workspace Settings -->
          <router-link class="context-menu-item" :to="getFullRoute('/payouts')" @click="closeUserMenu">
            <PhMoney :size="18" />
            <span>{{ t('sidebar.payouts') }}</span>
          </router-link>

          <router-link class="context-menu-item" :to="getFullRoute('/settings')" @click="closeUserMenu">
            <PhGear :size="18" />
            <span>{{ t('sidebar.settings') }}</span>
          </router-link>

          <router-link class="context-menu-item" :to="getFullRoute('/members')" @click="closeUserMenu">
            <PhUsers :size="18" />
            <span>{{ t('sidebar.teamMembers') }}</span>
          </router-link>

          <div class="context-menu-divider"></div>

          <!-- Help & Resources -->
          <a class="context-menu-item" href="https://docs.profitjam.io" target="_blank" rel="noopener" @click="closeUserMenu">
            <PhBook :size="18" />
            <span>{{ t('sidebar.docsResources') }}</span>
          </a>

          <button class="context-menu-item" @click="handleAskQuestion">
            <PhQuestion :size="18" />
            <span>{{ t('sidebar.askQuestion') }}</span>
          </button>

          <div class="context-menu-divider"></div>

          <!-- Sign Out -->
          <button class="context-menu-item logout" @click="handleLogout">
            <PhSignOut :size="18" />
            <span>{{ t('sidebar.signOut') }}</span>
          </button>
        </div>
      </Transition>

      <!-- Backdrop to close menu -->
      <div
        v-if="showUserMenu"
        class="menu-backdrop"
        @click="closeUserMenu"
      ></div>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal :open="showUpgradeModal" :feature="upgradeFeature" @close="showUpgradeModal = false" />
  </aside>
</template>

<style scoped>
.sidebar {
  width: 260px;
  height: 100vh;
  height: 100dvh;
  background-color: #FFFFFF;
  border-right: 1px solid #D6D6D6;
  transition: width 0.3s ease;
  z-index: 100;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
}

.sidebar.collapsed {
  width: 64px;
}

/* Logo Section */
.sidebar-logo {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  flex-shrink: 0;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-full {
  height: 18px;
  width: auto;
}

.logo-full :deep(.logo-full-svg) {
  height: 18px;
  width: auto;
}

.logo-icon :deep(.logo-icon-svg) {
  height: 20px;
  width: auto;
}

.sidebar.collapsed .sidebar-logo {
  padding: 16px 12px;
  justify-content: center;
}

.logo-icon-wrapper {
  width: 36px;
  height: 36px;
  border: 1px solid #D6D6D6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FFFFFF;
}

.logo-icon {
  height: 20px;
  width: auto;
}

.mobile-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #71717A;
  transition: all 0.2s ease;
}

.mobile-close-btn:hover {
  background-color: #F9F9F9;
  color: #021E30;
}

/* Vertical pull-tab on the right edge — sits at the same vertical level as
   the logo, just outside the sidebar's right border. Rectangular (not
   circular) so the shape doesn't compete with the collapsed logo icon's
   rounded square. Box stays still; only the inner caret rotates 180° in
   the collapsed state to read as "expand". */
.sidebar-collapse-toggle {
  position: absolute;
  top: 14px;
  right: -7px;
  width: 13px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  color: #71717A;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  z-index: 110;
  /* Hover-revealed — chevron stays invisible until the user hovers the
   * sidebar (any descendant of it triggers via the ancestor :hover, so
   * cursoring onto the chevron itself works too). Fade-out has a 1s
   * delay so quick mouse passes don't make it flash on and off; fade-in
   * is immediate (the :hover rule below overrides the delay). */
  opacity: 0;
  transition: opacity 150ms ease 1s, color 0.2s ease, top 0.3s ease;
}

.sidebar:hover .sidebar-collapse-toggle {
  opacity: 1;
  transition: opacity 150ms ease 0s, color 0.2s ease, top 0.3s ease;
}

/* Touch devices without hover: keep visible so the collapse affordance
 * remains discoverable. */
@media (hover: none) {
  .sidebar-collapse-toggle { opacity: 1; }
}

.sidebar-collapse-toggle:hover {
  color: #0097B2;
}

/* When the sidebar is collapsed the logo icon sits inside its 36×36
   rounded-square wrapper, which pushes the visible center down a few px;
   nudge the toggle down to match. */
.sidebar-collapse-toggle.collapsed {
  top: 20px;
}

.sidebar-collapse-toggle .toggle-arrow {
  transition: transform 0.3s ease;
}

.sidebar-collapse-toggle.collapsed .toggle-arrow {
  transform: rotate(180deg);
}

/* Menu Items Container. `flex: 1 1 0` + `min-height: 0` lets this child
   actually shrink to fit the remaining vertical space in the sidebar (the
   default `min-height: auto` on flex items would otherwise pin it to the
   intrinsic content height, defeating the `overflow-y: auto` and letting
   the menu spill past the bottom of short viewports). Keep the global 9px
   scrollbar gutter in both sidebar widths, but show its colors only while
   the pointer is over the navigation. */
.menu-items {
  flex: 1 1 0;
  min-height: 0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  overflow-y: auto;
  --app-scrollbar-thumb: transparent;
  --app-scrollbar-track: transparent;
}

.menu-items.has-section-back {
  padding-top: 6px;
}

.sidebar-pin-toggle {
  position: absolute;
  top: 14px;
  right: 22px;
  z-index: 111;
  width: 32px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #71717A;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.sidebar-pin-toggle:hover,
.sidebar-pin-toggle.pinned {
  color: #007D91;
  background-color: #E8FAFD;
}

.sidebar-pin-toggle.pinned {
  border-color: #B7EAF1;
}

.sidebar-pin-toggle:focus-visible {
  outline: 2px solid #0097B2;
  outline-offset: 2px;
}

.sidebar.collapsed .menu-items {
  /* Reserve room for the bar without moving the icon column left. */
  padding-right: 3px;
  padding-left: 13px;
}

.menu-items:hover {
  --app-scrollbar-thumb: #999;
  --app-scrollbar-track: #eee;
}

.sidebar-upgrade-slot {
  display: grid;
  grid-template-rows: 1fr;
  flex-shrink: 0;
  transition: grid-template-rows 0.3s ease;
}

.sidebar-upgrade-clip {
  min-height: 0;
  overflow: hidden;
}

.sidebar-upgrade-card {
  box-sizing: border-box;
  width: 236px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 24px;
  align-items: center;
  gap: 6px 8px;
  flex-shrink: 0;
  margin: 20px 12px 0;
  padding: 12px;
  border: 1px solid #d3e9ed;
  border-radius: 16px;
  background: radial-gradient(120% 90% at 50% 22%, #fff 0%, #e8fafd 58%, #d3f1f8 100%);
  color: var(--color-primary-dark);
  text-decoration: none;
  opacity: 1;
  visibility: visible;
  cursor: pointer;
  transition: opacity 0.18s ease 0.3s, visibility 0s linear 0.3s, transform 0.25s ease;
}

.sidebar-upgrade-card strong {
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.sidebar-upgrade-copy {
  display: grid;
  gap: 4px;
}

.sidebar-upgrade-card p {
  margin: 0;
  color: var(--color-primary-dark);
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.sidebar-upgrade-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-accent-cyan);
  color: var(--color-white);
}

.sidebar-upgrade-card:hover {
  transform: translateY(-2px);
}

.sidebar-upgrade-card:focus-visible {
  outline: 2px solid #021E30;
  outline-offset: 3px;
}

.sidebar.collapsed .sidebar-upgrade-slot {
  grid-template-rows: 0fr;
}

.sidebar.collapsed .sidebar-upgrade-card {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s ease, visibility 0s linear 0.1s;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-upgrade-slot,
  .sidebar-upgrade-card,
  .sidebar.collapsed .sidebar-upgrade-card { transition: none; }
  .sidebar-upgrade-card:hover { transform: none; }
}

/* Individual Menu Item */
.menu-item {
  width: 100%;
  height: 34px;
  padding: 6px 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease;
  background: transparent;
  border: none;
  font-family: inherit;
  /* Buttons center their text by default; links don't. Left-align at the
     base so button variants (back row, locked) match link items. */
  text-align: left;
}

.menu-item:hover {
  background-color: #F9F9F9;
}

.menu-item.active {
  background-color: #F9F9F9;
}

/* Locked Menu Item */
.menu-item.locked {
  opacity: 0.5;
  cursor: pointer;
  text-align: left;
}

.menu-item.locked:hover {
  opacity: 0.7;
  background-color: #F9F9F9;
}

.menu-item-lock {
  margin-left: auto;
  color: #71717A;
  flex-shrink: 0;
}

/* Menu Item Icon */
.menu-item-icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #021E30;
}

/* Ambient icon wiggle */
:deep(.icon-wiggle) {
  animation: icon-wiggle 0.5s ease-in-out 1;
}

@keyframes icon-wiggle {
  0%, 100% { transform: rotate(0deg); }
  20% { transform: rotate(-12deg); }
  40% { transform: rotate(10deg); }
  60% { transform: rotate(-6deg); }
  80% { transform: rotate(3deg); }
}

/* Menu Item Label */
.menu-item-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #021E30;
  width: 160px;
  margin: 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.2s ease;
}

.sidebar.collapsed .menu-item-label {
  opacity: 0;
  display: none;
}

.menu-item.active .menu-item-label {
  color: #18181B;
}

/* Add Button */
.menu-item-group { position: relative; width: 100%; }
.menu-item-group.has-add .menu-item { padding-right: 36px; }

.menu-item-add-btn {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  text-decoration: none;
  display: none;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background-color: #FFFFFF;
  padding: 2px;
  margin-left: auto;
  transition: all 0.3s ease;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  color: #021E30;
}

.menu-item-add-btn.visible,
.menu-item-group:hover .menu-item-add-btn,
.menu-item-add-btn:focus-visible {
  display: flex;
}

.menu-item-add-btn:hover {
  background-color: #F9F9F9;
  border-color: #021E30;
}

.sidebar.collapsed .menu-item-add-btn {
  display: none;
}

/* Collapsed Tooltips */
.menu-tooltip {
  display: none;
}

/* Collapsed-mode tooltip uses `position: fixed` so the menu can still
   scroll vertically without clipping the tooltip horizontally — the
   parent .menu-items keeps its `overflow-y: auto` from the default rule
   above. `--pj-tooltip-y` is stamped on the hovered item by
   handleItemEnter() and inherited here. `left: 72px` = sidebar width
   (64px) + 8px breathing room. */
.sidebar.collapsed .menu-tooltip {
  display: none;
  position: fixed;
  top: var(--pj-tooltip-y, 0);
  left: 72px;
  padding: 6px 10px;
  background-color: #021E30;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  border-radius: 6px;
  pointer-events: none;
  z-index: 200;
}

.sidebar.collapsed .menu-item {
  position: relative;
}

.sidebar.collapsed .menu-item:hover .menu-tooltip {
  display: block;
}

/* Section back action stays above the scrolling menu, aligned with its item
   icons. On tablet, leave room for the pin only while the sidebar is open. */
.settings-back-row {
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 12px 0;
  background-color: #FFFFFF;
}

.sidebar.collapsed .settings-back-row {
  padding-right: 3px;
  padding-left: 13px;
}

@media (min-width: 769px) and (max-width: 1400px) {
  .sidebar:not(.collapsed) .settings-back-row {
    padding-right: 54px;
  }
}

.settings-back-row .mobile-close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

/* Text Divider */
.menu-divider {
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  color: #71717A;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 8px 6px;
  width: 100%;
}

.sidebar.collapsed .menu-divider {
  padding: 12px 0 6px;
  margin-top: 6px;
}

.sidebar.collapsed .menu-divider span {
  display: none;
}

/* Sidebar Footer - Workspace Selector */
.sidebar-footer {
  padding: 12px;
  border-top: none;
  position: relative;
}

.user-workspace-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-workspace-selector:hover,
.user-workspace-selector.active {
  background-color: #F9F9F9;
}

.workspace-avatar-wrapper {
  position: relative;
  width: 42px;
  height: 42px;
  min-width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.credit-ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.workspace-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.workspace-name-label {
  font-size: 14px;
  font-weight: 600;
  color: #021E30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.my-workspace-label {
  font-size: 12px;
  font-weight: 400;
  color: #71717A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.caret-icon {
  color: #71717A;
  flex-shrink: 0;
}

/* Context Menu */
.user-context-menu {
  position: absolute;
  bottom: 0;
  left: 100%;
  margin-left: 8px;
  width: 280px;
  background-color: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 200;
}

/* Credits Section */
.credits-section {
  padding: 8px;
}

.credits-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #021E30;
}

.credits-label {
  font-size: 14px;
  font-weight: 600;
  flex: 1;
}

.upgrade-btn {
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  color: #0097B2;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.upgrade-btn:hover {
  background-color: #E6F7FA;
}

.credits-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 4px;
  padding-left: 26px;
}

.credits-sublabel {
  font-size: 12px;
  color: #71717A;
}

.credits-number {
  font-size: 14px;
  font-weight: 600;
  color: #021E30;
}

.context-menu-divider {
  height: 1px;
  background-color: #E5E5E5;
  margin: 8px 0;
}

.context-menu-item-wrapper {
  position: relative;
}

.context-menu-item {
  text-decoration: none;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #021E30;
  transition: background-color 0.2s ease;
  font-family: inherit;
  text-align: left;
}

.context-menu-item:hover {
  background-color: #F9F9F9;
}

.context-menu-item.has-submenu {
  position: relative;
}

.context-menu-item.has-submenu span {
  flex: 1;
}

.submenu-arrow {
  color: #71717A;
}

.context-menu-item.logout {
  color: #DC2626;
}

.context-menu-item.logout:hover {
  background-color: #FEE2E2;
}

/* Workspace Search */
.workspace-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-bottom: 1px solid #E5E5E5;
}

.workspace-search-icon {
  color: #A1A1AA;
  flex-shrink: 0;
}

.workspace-search-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 13px;
  font-family: inherit;
  color: #021E30;
  background: transparent;
}

.workspace-search-input::placeholder {
  color: var(--color-text-muted);
}

/* Workspaces Submenu */
.workspaces-submenu {
  position: fixed;
  width: 220px;
  display: flex;
  flex-direction: column;
  background-color: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px;
  z-index: 300;
}

.workspaces-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.workspace-option {
  text-decoration: none;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #021E30;
  transition: background-color 0.2s ease;
  font-family: inherit;
  text-align: left;
}

.workspace-option:hover {
  background-color: #F9F9F9;
}

.workspace-option.active {
  background-color: #F0F9FF;
}

.workspace-option.search-highlighted {
  background-color: #E6F7FA;
  box-shadow: inset 0 0 0 1px #0097B2;
}

.workspace-option-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.workspace-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-option-current {
  color: var(--color-text-secondary);
  font-size: 11px;
}

.no-workspaces {
  padding: 12px;
  text-align: center;
  color: #71717A;
  font-size: 13px;
}

.submenu-divider {
  height: 1px;
  background-color: #E5E5E5;
  margin: 4px 0;
}

.workspace-option-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #F4F4F5;
  color: #71717A;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workspace-option.create-workspace {
  color: #0097B2;
}

.workspace-option.create-workspace:hover {
  background-color: #E6F7FA;
}

.workspace-option.create-workspace .workspace-option-icon {
  background-color: #E6F7FA;
  color: #0097B2;
}

/* Menu Backdrop */
.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 150;
}

/* Menu Fade Transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}

/* Submenu Fade Transition */
.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.submenu-fade-enter-from,
.submenu-fade-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

/* Collapsed sidebar adjustments */
.sidebar.collapsed .workspace-info,
.sidebar.collapsed .caret-icon {
  display: none;
}

.sidebar.collapsed .user-workspace-selector {
  justify-content: center;
  padding: 8px 4px;
}

.sidebar.collapsed .user-context-menu {
  left: 100%;
  margin-left: 8px;
  bottom: 0;
}

/* Tablet Responsive */
@media (max-width: 1400px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 999;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .sidebar-upgrade-card { width: calc(100vw - 24px); }

  .sidebar {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    position: fixed;
    z-index: 999;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: 100%;
  }

  /* On mobile the sidebar is full-width, so the desktop "pop out to the right"
     position would put the menu off-screen. Drop it above the workspace selector instead. */
  .user-context-menu,
  .sidebar.collapsed .user-context-menu {
    left: 12px;
    right: 12px;
    bottom: calc(100% + 8px);
    margin-left: 0;
    width: auto;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
}
</style>
